/**
 * 后台管理系统主组件
 * 路由配置和整体布局
 */
import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Layout, Menu, Button, Avatar, Dropdown, Space, Badge, Modal, Form, Input, message, Result } from 'antd';
import {
  ToolOutlined, ShoppingCartOutlined, SwapOutlined,
  AppstoreOutlined, DashboardOutlined, LogoutOutlined,
  UserOutlined, BellOutlined, PrinterOutlined, DesktopOutlined,
  TeamOutlined, LockOutlined, CrownOutlined, IdcardOutlined, HomeOutlined,
} from '@ant-design/icons';
import { authApi, partsApi, employeeApi } from './services/api';
import { SystemConfigProvider, useSystemConfig } from './config/system';
import ErrorBoundary from './ErrorBoundary';

import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import RepairList from './pages/repair/RepairList';
import RepairDetail from './pages/repair/RepairDetail';
import SalesList from './pages/sales/SalesList';
import SalesDetail from './pages/sales/SalesDetail';
import ReturnList from './pages/return/ReturnList';
import ReturnDetail from './pages/return/ReturnDetail';
import PartsList from './pages/parts/PartsList';
import BigScreen from './pages/BigScreen';
import EmployeeManage from './pages/EmployeeManage';
import CustomerManage from './pages/CustomerManage';
import VipLevelManage from './pages/VipLevelManage';

const { Header, Sider, Content, Footer } = Layout;

// 菜单配置
const menuItems = [
  { key: '/dashboard', icon: <DashboardOutlined />, label: '工作台', perm: 'dashboard:view' },
  { key: '/bigscreen', icon: <DesktopOutlined />, label: '数据大屏', perm: 'bigscreen:view' },
  {
    key: 'system',
    icon: <TeamOutlined />,
    label: '系统管理',
    perm: 'employee:view',
    children: [
      { key: '/employees', icon: <TeamOutlined />, label: '员工管理', perm: 'employee:view' },
      { key: '/customers', icon: <IdcardOutlined />, label: '客户管理', perm: 'customer:view' },
      { key: '/vip-levels', icon: <CrownOutlined />, label: 'VIP等级', perm: 'vip:view' },
    ],
  },
  {
    key: 'repair',
    icon: <ToolOutlined />,
    label: '维修管理',
    perm: 'repair:view',
    children: [
      { key: '/repair/list', label: '维修工单', perm: 'repair:view' },
    ],
  },
  {
    key: 'sales',
    icon: <ShoppingCartOutlined />,
    label: '销售管理',
    perm: 'sales:view',
    children: [
      { key: '/sales/list', label: '销售订单', perm: 'sales:view' },
    ],
  },
  {
    key: 'return',
    icon: <SwapOutlined />,
    label: '返修管理',
    perm: 'return:view',
    children: [
      { key: '/return/list', label: '返修工单', perm: 'return:view' },
    ],
  },
  {
    key: 'parts',
    icon: <AppstoreOutlined />,
    label: '配件库存',
    perm: 'parts:view',
    children: [
      { key: '/parts/list', label: '配件管理', perm: 'parts:view' },
    ],
  },
];

export default function AppWrapper() {
  return (
    <ErrorBoundary>
      <SystemConfigProvider>
        <AppContent />
      </SystemConfigProvider>
    </ErrorBoundary>
  );
}

function AppContent() {
  const [collapsed, setCollapsed] = useState(false);
  const [adminInfo, setAdminInfo] = useState(null);
  const [stockAlerts, setStockAlerts] = useState(0);
  const [pwdModalOpen, setPwdModalOpen] = useState(false);
  const [pwdForm] = Form.useForm();
  const [backendOffline, setBackendOffline] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const sysConfig = useSystemConfig();

  // 后端健康检查
  useEffect(() => {
    fetch('/api/health')
      .then(res => res.json())
      .then(data => {
        if (data.code === 200) {
          setBackendOffline(false);
        }
      })
      .catch(() => {
        setBackendOffline(true);
      });
    // 每 30 秒检查一次
    const timer = setInterval(() => {
      fetch('/api/health')
        .then(res => res.json())
        .then(data => {
          if (data.code === 200) setBackendOffline(false);
        })
        .catch(() => setBackendOffline(true));
    }, 30000);
    return () => clearInterval(timer);
  }, []);

  // 检查登录状态
  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    const info = localStorage.getItem('admin_info');

    if (!token) {
      if (location.pathname !== '/login' && location.pathname !== '/register') {
        navigate('/login');
      }
      return;
    }

    if (info) {
      try {
        const parsed = JSON.parse(info);
        setAdminInfo(parsed);
      } catch { }
    }

    // 获取最新管理员信息（含权限列表）
    authApi.getInfo().then(res => {
      const adminData = res.data;
      setAdminInfo(adminData);
      localStorage.setItem('admin_info', JSON.stringify(adminData));
    }).catch(() => {
      if (location.pathname !== '/login') navigate('/login');
    });

    // 获取库存预警
    partsApi.stockAlert().then(res => {
      setStockAlerts(res.data?.alertCount || 0);
    }).catch(() => {});
  }, [navigate, location.pathname]);

  // 安全获取显示文字（防止渲染对象）
  const safeStr = (val, fallback = '') => {
    if (val === null || val === undefined) return fallback;
    if (typeof val === 'object') return fallback;
    return String(val);
  };

  // 检查是否有某权限（管理员拥有全部权限）
  const hasPerm = (perm) => {
    if (!adminInfo) return false;
    const perms = adminInfo.permissions || [];
    if (perms.includes('*')) return true;
    return perms.includes(perm);
  };

  // 根据权限过滤菜单
  const filterMenu = (items) => {
    return items
      .filter(item => !item.perm || hasPerm(item.perm))
      .map(item => ({
        ...item,
        children: item.children ? filterMenu(item.children) : undefined,
      }))
      .filter(item => !item.children || item.children.length > 0);
  };

  // ==================== 路由权限守卫 ====================
  // 客户只能访问 /dashboard，其他页面均拦截
  function ProtectedRoute({ requiredPerm, children }) {
    if (!adminInfo) return null;
    const role = adminInfo.role;
    const isCustomer = role === 'customer';

    if (isCustomer) {
      return (
        <Result
          status="403"
          title="无访问权限"
          subTitle="您当前是客户角色，仅可查看工作台"
          extra={<Button type="primary" onClick={() => navigate('/dashboard')}>返回工作台</Button>}
        />
      );
    }

    if (requiredPerm && !hasPerm(requiredPerm)) {
      return <Navigate to="/dashboard" />;
    }

    return children;
  }

  // 获取当前选中菜单
  const getSelectedKey = () => {
    const path = location.pathname;
    if (path === '/employees') return '/employees';
    if (path === '/customers') return '/customers';
    if (path === '/vip-levels') return '/vip-levels';
    if (path === '/bigscreen') return '/bigscreen';
    if (path === '/dashboard') return '/dashboard';
    return '/' + path.split('/')[1] + '/list';
  };
  const selectedKey = getSelectedKey();

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_info');
    navigate('/login');
  };

  // 修改密码
  const handleChangePwd = async () => {
    try {
      const values = await pwdForm.validateFields();
      await employeeApi.changePassword(values);
      message.success('密码修改成功，请重新登录');
      setPwdModalOpen(false);
      pwdForm.resetFields();
      setTimeout(() => handleLogout(), 1500);
    } catch { }
  };

  // 登录/注册页面不显示布局
  if (location.pathname === '/login' || location.pathname === '/register') {
    return (
      <Routes>
        <Route path="/login" element={<Login onLogin={setAdminInfo} />} />
        <Route path="/register" element={<Register />} />
        <Route path="*" element={<Navigate to="/login" />} />
      </Routes>
    );
  }

  // 大屏页面脱离标准布局，全屏独立展示
  if (location.pathname === '/bigscreen') {
    return (
      <Routes>
        <Route path="/bigscreen" element={<BigScreen />} />
        <Route path="*" element={<Navigate to="/bigscreen" />} />
      </Routes>
    );
  }

  return (
    <>
    <Layout style={{ minHeight: '100vh' }}>
      {/* 侧边栏 */}
      <Sider
        collapsible
        collapsed={collapsed}
        onCollapse={setCollapsed}
        style={{ background: '#001529' }}
      >
        <div style={{
          height: 64, display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#fff', fontSize: collapsed ? 14 : 16, fontWeight: 'bold',
          borderBottom: '1px solid rgba(255,255,255,0.1)',
        }}>
          {collapsed ? safeStr(sysConfig.companyShortName) : safeStr(sysConfig.companyShortName) + safeStr(sysConfig.systemTitle)}
        </div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[location.pathname]}
          defaultOpenKeys={adminInfo?.role === 'customer' ? [] : ['repair', 'sales', 'return', 'parts', 'system']}
          items={filterMenu(menuItems)}
          onClick={({ key }) => navigate(key)}
        />
      </Sider>

      <Layout>
        {/* 顶栏 */}
        <Header style={{
          background: '#fff', padding: '0 24px', display: 'flex',
          alignItems: 'center', justifyContent: 'space-between',
          borderBottom: '1px solid #f0f0f0', boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
        }}>
          <div style={{ fontSize: 16, fontWeight: 500 }}>
            {getPageTitle(location.pathname, sysConfig)}
          </div>

          <Space size="middle">
            <Button icon={<HomeOutlined />} type="link" href="http://www.lyjiayu.top" target="_blank">
              返回主页
            </Button>

            {adminInfo?.role !== 'customer' && (
              <Badge count={stockAlerts} size="small">
                <Button icon={<BellOutlined />} type="text" onClick={() => navigate('/parts/list')}>
                  库存预警
                </Button>
              </Badge>
            )}

            <Dropdown menu={{
              items: [
                { key: 'changePwd', icon: <LockOutlined />, label: '修改密码', onClick: () => { setPwdModalOpen(true); } },
                { key: 'print', icon: <PrinterOutlined />, label: '打印设置' },
                { type: 'divider' },
                { key: 'logout', icon: <LogoutOutlined />, label: '退出登录', danger: true, onClick: handleLogout },
              ],
            }}>
              <Space style={{ cursor: 'pointer' }}>
                <Avatar icon={<UserOutlined />} />
                <span>{safeStr(adminInfo?.realName || adminInfo?.username, '管理员')}</span>
              </Space>
            </Dropdown>
          </Space>
        </Header>

        {/* 内容区 */}
        <Content style={{ margin: 16, background: '#fff', borderRadius: 8, padding: 24, minHeight: 280 }}>
          {/* 后端离线警告 */}
          {backendOffline && (
            <div style={{
              background: '#fff2f0', border: '1px solid #ffccc7',
              borderRadius: 6, padding: '10px 16px', marginBottom: 16,
              fontSize: 13, color: '#cf1322', display: 'flex', alignItems: 'center',
            }}>
              <span style={{ fontSize: 16, marginRight: 8 }}>⚠️</span>
              <span>后端服务离线 — 数据无法加载。请确认服务器 Node.js 进程正在运行（端口 3000）。</span>
            </div>
          )}
          <Routes>
            <Route path="/" element={<Navigate to="/dashboard" />} />
            {/* 工作台 — 所有人可访问 */}
            <Route path="/dashboard" element={<Dashboard />} />
            {/* 以下路由仅非客户角色可访问 */}
            <Route path="/repair/list" element={<ProtectedRoute requiredPerm="repair:view"><RepairList /></ProtectedRoute>} />
            <Route path="/repair/detail/:id" element={<ProtectedRoute requiredPerm="repair:view"><RepairDetail /></ProtectedRoute>} />
            <Route path="/sales/list" element={<ProtectedRoute requiredPerm="sales:view"><SalesList /></ProtectedRoute>} />
            <Route path="/sales/detail/:id" element={<ProtectedRoute requiredPerm="sales:view"><SalesDetail /></ProtectedRoute>} />
            <Route path="/return/list" element={<ProtectedRoute requiredPerm="return:view"><ReturnList /></ProtectedRoute>} />
            <Route path="/return/detail/:id" element={<ProtectedRoute requiredPerm="return:view"><ReturnDetail /></ProtectedRoute>} />
            <Route path="/parts/list" element={<ProtectedRoute requiredPerm="parts:view"><PartsList /></ProtectedRoute>} />
            <Route path="/employees" element={<ProtectedRoute requiredPerm="employee:view"><EmployeeManage /></ProtectedRoute>} />
            <Route path="/customers" element={<ProtectedRoute requiredPerm="customer:view"><CustomerManage /></ProtectedRoute>} />
            <Route path="/vip-levels" element={<ProtectedRoute requiredPerm="vip:view"><VipLevelManage /></ProtectedRoute>} />
            <Route path="/bigscreen" element={<Navigate to="/bigscreen" />} />
            <Route path="/login" element={<Navigate to="/dashboard" />} />
          </Routes>
        </Content>

        {/* 全局页脚 — 版权信息 */}
        <Footer style={{
          textAlign: 'center',
          padding: '12px 24px',
          fontSize: 12,
          color: '#999',
          background: '#fafafa',
          borderTop: '1px solid #f0f0f0',
        }}>
          {String('© 2026 嘉宇信息技术公司 版权所有 | www.lyjiayu.top | 客服电话：0597-2655111 | ')}
          <a href="https://beian.miit.gov.cn/" target="_blank" rel="noopener noreferrer" style={{color:'#999'}}>{String('闽ICP备2026019676号')}</a>
        </Footer>
      </Layout>
    </Layout>

    {/* 修改密码弹窗 */}
    <Modal
      title={<Space><LockOutlined />修改密码</Space>}
      open={pwdModalOpen}
      onOk={handleChangePwd}
      onCancel={() => { setPwdModalOpen(false); pwdForm.resetFields(); }}
      okText="确认修改"
      cancelText="取消"
      destroyOnClose
    >
      <Form form={pwdForm} layout="vertical" style={{ marginTop: 16 }}>
        <Form.Item
          name="oldPassword" label="旧密码"
          rules={[{ required: true, message: '请输入当前密码' }]}
        >
          <Input.Password placeholder="输入旧密码" />
        </Form.Item>
        <Form.Item
          name="newPassword" label="新密码"
          rules={[
            { required: true, message: '请输入新密码' },
            { min: 6, message: '至少6位' },
          ]}
        >
          <Input.Password placeholder="输入新密码" />
        </Form.Item>
      </Form>
    </Modal>
    </>
  );
}

function getPageTitle(pathname, sysConfig) {
  const map = {
    '/dashboard': '工作台',
    '/bigscreen': '数据大屏',
    '/repair/list': '维修工单管理',
    '/repair/detail': '维修工单详情',
    '/sales/list': '销售订单管理',
    '/sales/detail': '销售订单详情',
    '/return/list': '返修工单管理',
    '/return/detail': '返修工单详情',
    '/parts/list': '配件库存管理',
    '/employees': '员工管理',
    '/customers': '客户管理',
    '/vip-levels': 'VIP等级设置',
  };
  for (const [key, val] of Object.entries(map)) {
    if (pathname.startsWith(key)) return val;
  }
  return String(sysConfig?.companyShortName || '') + String(sysConfig?.systemTitle || '');
}
